import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import {
  Alert,
  AttachmentClip,
  AttachmentGallery,
  AttachmentRefusals,
  AttachmentStrip,
  Avatar,
  backLinkClasses,
  Badge,
  Button,
  Card,
  cn,
  ConversationMessage,
  CopyButton,
  Icon,
  Modal,
  Page,
  PageHeader,
  readAttachmentContent,
  TextArea,
  useAttachmentIntake,
  useAttachments,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import { ticketPriorityLabel } from '@evogard/domain';
import type { Ticket, Message, Priority } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { StatusForm } from './StatusForm.js';
import { TransferForm } from './TransferForm.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  attachmentItem,
  Deadline,
  TicketStatusBadge,
  formatInstant,
  refusalFor,
  useIsConsultant,
  viewerFrom,
} from './common.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * A demanda inteira em uma tela: situação, prazo, linha do tempo, resposta.
 *
 * **Só se lê e se responde aqui — não se muda o fluxo.** Decisão de 09/09:
 * quem abre uma demanda para acompanhar não deveria também poder desviá-la de
 * fila ou trocar o responsável por engano.
 *
 * - **Atribuir é ato da mesa** (`TicketsScreen.tsx`). Encaminhar voltou ao
 *   detalhe em 23/09/2026, ao lado de "Mudar situação": quem está com a
 *   demanda decide passá-la adiante depois de ler a conversa, e é aqui que ele
 *   a lê.
 * - **Mudar situação voltou em 23/09/2026**: botão no cabeçalho e item no
 *   painel da mesa, o mesmo `StatusForm` nos dois. De 09/09 a 22/09 a rota
 *   (`changeStatus`) existia sem tela nenhuma — não havia como concluir uma
 *   demanda pela interna.
 *
 * `PermissionGuard` continua recusando do lado do servidor de qualquer forma
 * — esconder o controle é sobre onde a ação mora, não sobre quem pode fazê-la.
 */
export function TicketScreen() {
  const { id } = useParams({ from: '/authenticated/tickets/$id' });
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const ticketQuery = useQuery({
    queryKey: ['demandas', 'demanda', tenantId, id],
    queryFn: async () => {
      /* `/…/abc` não é um id: a resposta é a mesma do 404, sem ida ao servidor */
      if (!Number.isInteger(Number(id)))
        throw new NotFoundError('Este ticket não existe nesta base');
      const r = await api.tickets.get({ params: { id: Number(id) } });
      if (r.status === 404) throw new NotFoundError('Este ticket não existe nesta base');
      /*
       * 403, e não 404: a demanda existe, a base é a certa e ela está no
       * escopo — só que quem está logado não aderiu a ela (não é quem abriu,
       * não é o responsável, não lidera o setor, não tem `ver_qualquer`). A
       * CONVERSA continua fechada, e o servidor não a mandou.
       *
       * Desde 23/09/2026 a ficha não para aí: ela pede o `preview` — o resumo
       * que a mesma pessoa já lê na linha da mesa — e desenha a aba Resumo,
       * com Interações, Documentos e o link cinzas e sem clique (pedido do
       * dono). O motivo do 403 vai junto, e a tela o mostra: é ele que diz o
       * caminho ("adira a ela para ler a conversa").
       */
      if (r.status === 403) {
        const reason =
          (r.body as { message?: string }).message ??
          'Você não está autorizado a ler a conversa deste ticket.';
        const preview = await api.tickets.preview({ params: { id: Number(id) } });
        if (preview.status !== 200) throw new Error(reason);
        const { attachments: _attachments, ...summary } = preview.body;
        return {
          ticket: { ...summary, messages: [], transitions: [] } satisfies Ticket,
          locked: reason,
        };
      }
      return { ticket: ok(r, 200, 'Não foi possível carregar o ticket.'), locked: null };
    },
    retry: false,
  });

  /*
   * `isPending`, não `isLoading`.
   *
   * `isLoading` é `isPending && isFetching` — e uma consulta que o React Query
   * PAUSA por estar sem rede (`networkMode: 'online'`, o padrão) fica pendente
   * sem estar buscando. Com `isLoading`, os três estados davam `false` ao mesmo
   * tempo, o código caía no `data!` e lia `undefined`: o associado que abre uma
   * demanda no elevador recebia um `TypeError` no lugar do esqueleto, e a tela
   * de falha ainda diagnosticava "a Evogard não respondeu" para uma queda de
   * wi-fi.
   */
  if (ticketQuery.isPending) return <Skeleton />;

  if (isNotFound(ticketQuery.error)) {
    return (
      <MissingRecord
        title={ticketQuery.error.message}
        list={{ to: '/tickets', label: 'Ver os tickets' }}
      />
    );
  }

  if (ticketQuery.isError) {
    return (
      <div className="max-w-3xl">
        <Alert tone="erro" title="Não foi possível abrir o ticket.">
          {ticketQuery.error.message}{' '}
          <Link to="/tickets" activeOptions={{ exact: true }} className="underline">
            Voltar para a lista
          </Link>
          .
        </Alert>
      </div>
    );
  }

  const { ticket, locked } = ticketQuery.data!;
  return <TicketDetail ticket={ticket} locked={locked} />;
}

/**
 * `locked` é o motivo de a conversa estar fechada para quem olha — ou `null`.
 * Com motivo, só a aba Resumo abre; Interações, Documentos e "Mostrar o link"
 * ficam cinzas e sem clique, e o motivo aparece no topo do resumo.
 */
function TicketDetail({ ticket: d, locked }: { ticket: Ticket; locked: string | null }) {
  const { data: context } = useSessionContext();

  /*
   * Responder tem DOIS portões, e eles dizem coisas diferentes.
   *
   * `responder` é a permissão do grupo — o piso, e quem não a tem não escreve
   * em demanda nenhuma. `refusalToReply` é o papel: desde 17/09 só o
   * RESPONSÁVEL responde, e quem lê a conversa sem ser ele (o solicitante, o
   * líder do setor) vê a linha do tempo inteira e não vê o formulário.
   *
   * Por isso o formulário some em vez de ficar desabilitado: um campo de texto
   * que não aceita texto é pior do que campo nenhum, e a frase ao lado diz o
   * que fazer.
   */
  const canReply = can(context, 'tickets.tickets', 'responder');
  /* o consultor escreve na demanda que abriu — ver `TicketViewer.consultant` */
  const isConsultant = useIsConsultant();
  const refusalToReply = refusalFor(
    'reply',
    { ...d, department: d.department },
    viewerFrom(context, isConsultant),
    context?.user.id,
  );

  const documents = d.messages.flatMap((m) =>
    m.attachments.map((a) => ({ attachment: a, message: m })),
  );

  /*
   * Mudar a situação (23/09/2026). O detalhe deixou de mudar o fluxo em 09/09
   * — atribuir e encaminhar são da mesa —, e a situação ficou sem tela
   * nenhuma. Ela volta aqui porque é o lugar onde se conclui: quem acabou de
   * responder está NESTA tela. O botão só aparece para quem pode (a permissão
   * `concluir` e o papel do domínio), e só enquanto há para onde ir — a mesa
   * mostra o mesmo item com o motivo quando não pode.
   */
  const [changingStatus, setChangingStatus] = useState(false);
  const canChangeStatus =
    can(context, 'tickets.tickets', 'concluir') &&
    d.transitions.length > 0 &&
    refusalFor('close', d, viewerFrom(context, isConsultant), context?.user.id) === false;

  /*
   * Encaminhar, também no detalhe (23/09/2026): quem está com a demanda
   * acabou de ler a conversa aqui, e é aqui que ele decide passá-la adiante.
   * O mesmo gate da mesa — a permissão `encaminhar` e o papel do domínio.
   */
  const [transferring, setTransferring] = useState(false);
  const canTransfer =
    can(context, 'tickets.tickets', 'encaminhar') &&
    refusalFor('transfer', d, viewerFrom(context, isConsultant), context?.user.id) ===
      false;

  return (
    <Page measure="cheia">
      <PageHeader
        back={
          <Link
            to="/tickets"
            activeOptions={{ exact: true }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Voltar aos tickets
          </Link>
        }
        /*
         * Código, situação e prioridade ACIMA do título, como no desenho de
         * 05/09. Eram o código embaixo e a situação ao lado; com três etiquetas
         * ao lado, o título ia para a segunda linha em qualquer tela estreita.
         */
        eyebrow={
          <>
            <span className="rounded-pequeno border border-borda bg-superficie px-2 py-1 font-mono text-xs text-texto-suave">
              {d.code}
            </span>
            <TicketStatusBadge situacao={d.situacao} />
            <TicketPriorityBadge priority={d.priority} />
          </>
        }
        title={d.title}
        actions={
          (canTransfer || canChangeStatus) && (
            <div className="flex flex-wrap gap-2">
              {canTransfer && (
                <Button
                  icon="seta-direita"
                  variant="contorno"
                  onClick={() => setTransferring(true)}
                >
                  Encaminhar
                </Button>
              )}
              {canChangeStatus && (
                <Button
                  icon="atualizar"
                  variant="contorno"
                  onClick={() => setChangingStatus(true)}
                >
                  Mudar situação
                </Button>
              )}
            </div>
          )
        }
      >
        Aberto em {formatInstant(d.createdAt)} por {d.requester.nome} ·{' '}
        {d.department?.nome ? (
          <>fila {d.department.nome}</>
        ) : (
          'na fila de todos que podem responder'
        )}
      </PageHeader>

      {canTransfer && (
        <Modal
          open={transferring}
          onOpenChange={setTransferring}
          title="Encaminhar"
          descricao={`Ticket ${d.code}`}
        >
          <TransferForm ticket={d} onDone={() => setTransferring(false)} />
        </Modal>
      )}

      {canChangeStatus && (
        <Modal
          open={changingStatus}
          onOpenChange={setChangingStatus}
          title="Mudar situação"
          descricao={`Ticket ${d.code}`}
        >
          <StatusForm ticket={d} onDone={() => setChangingStatus(false)} />
        </Modal>
      )}

      <Card bleed>
        <Tabs defaultValue="resumo">
          <TabsList>
            <TabsTrigger value="resumo">Resumo</TabsTrigger>
            {/*
             * A contagem no rótulo, como no desenho. Ela responde antes do
             * clique a pergunta que faz alguém clicar — "tem alguma coisa
             * aí?" —, e é o que evita abrir três abas para descobrir que
             * duas estão vazias.
             */}
            {/*
             * Sem ter aderido, as duas abas ficam cinzas e sem clique — e SEM
             * a contagem: o servidor não mandou a conversa, e "Interações · 0"
             * afirmaria uma conversa vazia que ninguém mediu.
             */}
            <TabsTrigger
              value="interacoes"
              disabled={locked !== null}
              title={locked ?? undefined}
            >
              {locked !== null ? 'Interações' : `Interações · ${d.messages.length}`}
            </TabsTrigger>
            <TabsTrigger
              value="documentos"
              disabled={locked !== null}
              title={locked ?? undefined}
            >
              {locked !== null ? 'Documentos' : `Documentos · ${documents.length}`}
            </TabsTrigger>
          </TabsList>

          {/*
           * `forceMount` nos três painéis, e o Radix esconde o inativo com
           * `hidden` em vez de desmontá-lo.
           *
           * Sem isso, trocar de aba **joga fora o que estava sendo escrito**:
           * o campo de resposta é controlado, e o `useState` dele morre com o
           * painel. Quem escreve meia resposta, vai conferir um anexo e volta,
           * volta para um campo vazio.
           *
           * **Não é o conserto da instabilidade** que o `CLAUDE.md` registra em
           * `internal.tickets.spec.ts`. Foi tentado como hipótese e medido:
           * 2 falhas em 3 execuções antes, 2 em 3 depois. Fica porque o
           * rascunho preservado vale por si; a causa daquela falha continua
           * sendo outra, e continua aberta.
           */}
          <TabsContent
            value="resumo"
            forceMount
            className="p-4 data-[state=inactive]:hidden"
          >
            {locked !== null && (
              <Alert tone="informacao" className="mb-4">
                {locked}
              </Alert>
            )}
            <p className="rotulo text-texto-tenue">Descrição</p>
            <p className="mt-2 whitespace-pre-wrap text-sm">{d.descricao}</p>

            <dl className="mt-5 grid gap-x-6 gap-y-4 border-t border-borda-suave pt-4 sm:grid-cols-2 lg:grid-cols-4">
              {/*
               * "Aberta por" é quem abriu; "Solicitante" é quem PEDIU a um
               * colaborador que abrisse (`demandas-45`) — e só aparece quando
               * não é a mesma pessoa, que é o caso comum.
               */}
              <Fact label="Aberto por">
                {d.requester.nome}
                {d.requester.tipo === 'associado' && (
                  <Badge tone="informacao" className="ml-2">
                    Associado
                  </Badge>
                )}
              </Fact>
              {d.onBehalfOf && <Fact label="Solicitante">{d.onBehalfOf.nome}</Fact>}
              {d.vehicle && (
                <Fact label="Placa">
                  {/* sem rótulo do espelho, o código cru — não uma placa inventada */}
                  <span className="font-mono">
                    {d.vehicle.placa ?? `Veículo ${d.vehicle.code}`}
                  </span>
                  {(d.vehicle.marca || d.vehicle.modelo) && (
                    <span className="block text-xs font-normal text-texto-suave">
                      {[d.vehicle.marca, d.vehicle.modelo].filter(Boolean).join(' ')}
                    </span>
                  )}
                </Fact>
              )}
              <Fact label="Categoria">
                {d.categoria?.nome ?? (
                  <span className="text-texto-suave">Sem categoria</span>
                )}
              </Fact>
              <Fact label="Prazo">
                <Deadline dueAt={d.dueAt} completed={Boolean(d.completedAt)} />
              </Fact>
              <Fact label={d.completedAt ? 'Encerrado em' : 'Aberto em'}>
                {formatInstant(d.completedAt ?? d.createdAt)}
              </Fact>
            </dl>

            <TrackingLink id={d.id} locked={locked !== null} />
          </TabsContent>

          <TabsContent
            value="interacoes"
            forceMount
            className="p-4 data-[state=inactive]:hidden"
          >
            {d.messages.length === 0 ? (
              <p className="text-sm text-texto-suave">
                Nenhuma mensagem ainda. O que for respondido aparece aqui, na ordem — do
                mais antigo para o mais recente.
              </p>
            ) : (
              <ol className="flex flex-col gap-3">
                {d.messages.map((m) => (
                  <MessageNaRow key={m.id} message={m} ticketId={d.id} />
                ))}
              </ol>
            )}
            {canReply &&
              (refusalToReply === false ? (
                <div className="mt-4">
                  <ResponseForm ticket={d} />
                </div>
              ) : (
                <p className="mt-4 rounded-borda bg-superficie-suave px-4 py-3 text-sm text-texto-suave">
                  {refusalToReply}
                </p>
              ))}
          </TabsContent>

          <TabsContent
            value="documentos"
            forceMount
            className="p-4 data-[state=inactive]:hidden"
          >
            {documents.length === 0 ? (
              /*
               * "Nenhum ANEXADO A UMA RESPOSTA", e não "nenhum anexado".
               *
               * `documents` sai de `d.messages`, e o anexo com `message_id`
               * nulo — o que veio junto da abertura — não pertence a mensagem
               * nenhuma: `messagesOf` filtra por `a.messageId === m.id`. É a
               * pendência que o `CLAUDE.md` registra, e enquanto ela existir
               * esta aba pode contar zero sobre uma demanda que TEM anexo.
               *
               * A contagem no rótulo continua sendo a dos documentos que a tela
               * sabe listar; o que não pode é a frase afirmar a ausência de algo
               * que ela não consegue ver.
               */
              <p className="text-sm text-texto-suave">
                Nenhum documento anexado a uma resposta. O que vier junto de uma resposta
                aparece aqui — anexos enviados na abertura ainda não chegam a esta lista.
              </p>
            ) : (
              <AttachmentGallery
                items={documents.map(({ attachment, message }) =>
                  attachmentItem(
                    attachment,
                    d.id,
                    `${message.author.nome} · ${formatInstant(message.createdAt)}`,
                  ),
                )}
              />
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </Page>
  );
}

/** Um par rótulo/valor do resumo — versalete em cima, valor embaixo. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="rotulo text-texto-tenue">{label}</dt>
      {/*
       * `break-words` e não `truncate`: nome de categoria e de pessoa são o
       * conteúdo do fato, e cortá-los com reticências ("Categoria e2e-mtpn…")
       * transforma a resposta numa charada. Quebrar é feio uma vez; truncar
       * está errado sempre.
       */}
      <dd className="mt-1 break-words text-sm font-semibold">{children}</dd>
    </div>
  );
}

/**
 * A prioridade, que a mesa filtra e o detalhe não mostrava.
 *
 * `alta` é `alerta` e `baixa` é `neutro`; `normal` **não desenha nada**. Um
 * selo "Prioridade normal" em toda demanda é ruído — o normal é o que não
 * precisa ser dito, e uma etiqueta em 100% das linhas deixa de separar
 * qualquer coisa.
 */
function TicketPriorityBadge({ priority }: { priority: Priority }) {
  if (priority === 'normal') return null;
  return (
    <Badge tone={priority === 'alta' ? 'alerta' : 'neutro'}>
      Prioridade {ticketPriorityLabel(priority).toLowerCase()}
    </Badge>
  );
}

/**
 * Uma mensagem da conversa, como CHAT (23/09/2026): as minhas à direita, as
 * dos outros à esquerda, as do sistema no meio. A peça é `ConversationMessage`
 * de `@evogard/ui`, a mesma do portal — ver o docblock de lá para as cores.
 *
 * "Minha" é o autor contra o usuário da sessão, que é o dado que a tela já
 * tem. A nota interna continua âmbar dos dois lados, e o selo em texto fica:
 * é o que `internal.departments.spec.ts` e `tickets.e2e.test.ts` cobram.
 */
function MessageNaRow({ message: m, ticketId }: { message: Message; ticketId: number }) {
  const { data: context } = useSessionContext();
  const mine = m.author.id !== null && m.author.id === context?.user.id;
  const system = m.author.tipo === 'sistema';

  return (
    <ConversationMessage
      side={system ? 'sistema' : mine ? 'meu' : 'outro'}
      tone={m.internal ? 'nota' : m.fromLead ? 'lideranca' : 'padrao'}
      author={mine ? 'Você' : m.author.nome}
      timestamp={formatInstant(m.createdAt)}
      avatar={<Avatar nome={m.author.nome} tone="escura" />}
      badges={
        <>
          {m.internal && <Badge tone="alerta">Nota interna</Badge>}
          {m.fromLead && <Badge tone="informacao">Liderança</Badge>}
        </>
      }
      footer={
        /*
         * A grade, e não a pastilha com o nome: a prévia responde antes do
         * clique, e é a mesma peça da aba "Documentos" e do campo de envio.
         */
        m.attachments.length > 0 && (
          <AttachmentGallery
            className="mt-2.5 border-t border-borda-suave pt-2.5"
            items={m.attachments.map((a) => attachmentItem(a, ticketId))}
          />
        )
      }
    >
      {m.body}
    </ConversationMessage>
  );
}

/**
 * Responder ou deixar nota interna.
 *
 * A escolha é uma caixa marcada, e não dois botões parecidos: a diferença entre
 * "o solicitante lê" e "só a equipe lê" precisa estar visível **antes** de
 * enviar, não decidida por qual botão a pessoa acertou.
 */
function ResponseForm({ ticket: d }: { ticket: Ticket }) {
  const client = useQueryClient();
  /* o consultor não é a equipe: escreve na conversa, e não tem nota interna */
  const isConsultant = useIsConsultant();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * A lista de escolhidos é do `useAttachments`, e com ela vêm as `blob:` URLs
   * que desenham a prévia — criadas na escolha e revogadas na remoção. Era um
   * `useState<PickedFile[]>` com o base64 já lido na escolha; ler os vinte
   * megabytes de um vídeo para uma lista que a pessoa ainda pode esvaziar é
   * trabalho jogado fora, e o base64 passou a ser lido no envio.
   */
  const picked = useAttachments();
  /**
   * A mensagem que JÁ foi criada, quando o envio parou no meio.
   *
   * O anexo precisa de uma mensagem para pendurar (ver o comentário do envio),
   * então o texto vai primeiro e os arquivos depois. Se um arquivo falhar, a
   * resposta já está lá: repetir o envio inteiro publicaria a mesma resposta
   * duas vezes na linha do tempo do solicitante. Guardando o id, o "tentar de
   * novo" manda só o que faltou.
   */
  const [sentMessageId, setSentMessageId] = useState<number | null>(null);

  const sent = sentMessageId !== null;

  const send = useMutation({
    mutationFn: async () => {
      /*
       * A ordem é obrigatória, e não é preferência: o anexo com `message_id`
       * nulo é gravado, ocupa espaço e **nunca aparece** — o mapeamento da API
       * casa cada anexo com a mensagem dele (`a.messageId === m.id`), e o nulo
       * não casa com nenhuma. Por isso a resposta vai primeiro: é ela que
       * produz o id que torna o arquivo visível.
       */
      let messageId = sentMessageId;
      if (messageId === null) {
        const r = await api.tickets.reply({
          params: { id: d.id },
          body: { body: body.trim(), internal },
        });
        messageId = ok(r, 201, 'Não foi possível enviar.').id;
        setSentMessageId(messageId);
      }

      /*
       * Um de cada vez, e a lista encolhe a cada sucesso.
       *
       * Em paralelo, três anexos de 20 MB disputam a mesma subida numa rede
       * móvel e chegam os três mais devagar; e o que sobreviveria de um envio
       * parcial viraria adivinhação. Serial, o que subiu sai da lista e o que
       * falhou continua nela, exatamente para o próximo clique.
       */
      for (const attachment of [...picked.items]) {
        /*
         * O base64 é lido AQUI, e não na escolha.
         *
         * Lendo na escolha, um vídeo de 15 MB vira ~20 MB de texto na memória
         * da aba no instante em que a pessoa o escolhe — e some junto se ela
         * remover o arquivo antes de enviar. A prévia do ladrilho não precisa
         * dele: ela desenha da `blob:` URL, que não copia nada.
         */
        const content = await readAttachmentContent(attachment.file);
        const r = await api.tickets.attach({
          params: { id: d.id },
          body: {
            nome: attachment.nome,
            typeMime: attachment.typeMime,
            content,
            messageId,
          },
        });
        ok(r, 201, `Não foi possível anexar ${attachment.nome}.`);
        picked.remove(attachment.key);
      }
    },
    onSuccess: () => {
      setBody('');
      picked.clear();
      setSentMessageId(null);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
    /*
     * Sempre, inclusive na falha: quando o texto passou e o anexo não, a linha
     * do tempo mudou — e a tela que não recarrega deixa a pessoa achando que
     * nada foi enviado, prestes a escrever tudo de novo.
     */
    onSettled: () => void client.invalidateQueries({ queryKey: ['demandas'] }),
  });

  /* escolher, arrastar e colar passam pela mesma conferência */
  const intake = useAttachmentIntake({
    count: picked.items.length,
    onAdd: picked.add,
    disabled: send.isPending,
  });

  function remove(key: string) {
    picked.remove(key);
    /*
     * Tirar o último arquivo depois de a resposta já ter ido não deixa nada
     * para enviar — o formulário volta ao estado limpo em vez de ficar preso
     * pedindo um "tentar de novo" sem objeto.
     *
     * `picked.items` ainda é a lista de ANTES aqui dentro: o estado do React só
     * troca no próximo render. Por isso a conta é "sobrou um, e ele está
     * saindo", e não `items.length === 0`.
     */
    if (sent && picked.items.length === 1) {
      setBody('');
      setSentMessageId(null);
      setError(null);
    }
  }

  const nothingToSend = sent ? picked.items.length === 0 : !body.trim();

  return (
    /*
     * Uma caixa só: o texto em cima, os anexos no meio, a barra de ações
     * embaixo — o compositor do desenho de 05/09. Era um `Card` com título,
     * campo emoldurado, caixa de seleção e botão, cada um num plano; ali dentro
     * da aba, com o balão das mensagens logo acima, virava a quarta borda da
     * mesma coluna.
     *
     * Do desenho, "@ Mencionar" continua **de fora**: menção não existe em
     * lugar nenhum do produto, e botão que não faz nada é pior do que botão que
     * falta.
     *
     * A frase de formatos e teto fica na `AttachmentStrip`, e não na barra de
     * ações: uma linha permanente de regra entre três controles é ruído em todo
     * envio para avisar sobre o raro. Ela aparece com o contador, logo abaixo
     * dos arquivos escolhidos — e o aviso de recusa diz o resto.
     */
    <form
      /*
       * A caixa inteira recebe o arquivo arrastado (23/09/2026), como o chat do
       * WhatsApp Web — e se realça enquanto ele está por cima.
       */
      {...intake.dropHandlers}
      className={cn(
        'rounded-grande border bg-superficie transition-colors',
        intake.over ? 'border-primaria-texto bg-primaria-suave' : 'border-borda',
      )}
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        send.mutate();
      }}
    >
      {error && (
        <div className="p-3 pb-0">
          <Alert tone="erro">{error}</Alert>
        </div>
      )}

      {sent && (
        <div className="p-3 pb-0">
          <Alert tone="alerta" title="A resposta já foi enviada.">
            Faltou o anexo. O texto não vai outra vez — só os arquivos abaixo.
          </Alert>
        </div>
      )}

      <TextArea
        bare
        label={internal ? 'Nota (não sai para o solicitante)' : 'Resposta'}
        required
        maxLength={10_000}
        counter
        value={body}
        readOnly={sent}
        onChange={(e) => setBody(e.target.value)}
        /* Ctrl+V de um print vira anexo; texto colado continua no campo */
        onPaste={intake.onPaste}
        placeholder={
          internal
            ? 'O que a equipe precisa saber e o solicitante não vê.'
            : 'O que o solicitante vai ler. Arraste um arquivo ou cole um print para anexar.'
        }
      />

      {picked.items.length > 0 && (
        <div className="border-t border-borda-suave px-3.5 py-3">
          <AttachmentStrip
            items={picked.items}
            onRemove={remove}
            disabled={send.isPending}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-borda-suave bg-superficie-suave px-3.5 py-2.5">
        {/*
         * A caixa fica na barra, ao lado do botão, e não acima do campo: a
         * escolha entre "o solicitante lê" e "só a equipe lê" precisa estar
         * visível no instante do envio, que é onde a mão está.
         */}
        {!isConsultant && (
          <label className="flex items-center gap-2 text-[0.8125rem] pointer-coarse:min-h-11">
            <input
              type="checkbox"
              checked={internal}
              onChange={(e) => setInternal(e.target.checked)}
              className="size-4 rounded border-borda-forte accent-primaria-texto"
            />
            Nota interna — só a equipe vê
          </label>
        )}

        {/*
         * O clipe VOLTOU para a barra em 23/09/2026, pequeno, no lugar da área
         * tracejada de 150px que ocupava a caixa mesmo sem arquivo nenhum.
         * Arrastar e colar continuam sem botão — valem na caixa inteira.
         */}
        <AttachmentClip intake={intake} disabled={send.isPending} />

        <Button
          type="submit"
          size="sm"
          className="ml-auto"
          loading={send.isPending}
          disabled={nothingToSend}
        >
          {sent ? 'Enviar o anexo' : internal ? 'Salvar nota' : 'Enviar resposta'}
        </Button>
      </div>

      {internal && (
        <div className="border-t border-borda-suave p-3">
          <Alert tone="alerta">
            Esta nota não aparece no portal do associado nem no acompanhamento por código.
          </Alert>
        </div>
      )}

      <AttachmentRefusals intake={intake} />
    </form>
  );
}

/**
 * O link de acompanhamento, na ficha.
 *
 * ## Por que é consulta própria, e por que ela só sai no clique
 *
 * O token é o segredo, e não vem em `GET /tickets/:id`: pô-lo lá o entregaria a
 * toda abertura de ficha, guardado no cache do TanStack Query e visível no
 * devtools de quem abrisse. Aqui a rota só é chamada quando alguém pede o link —
 * `enabled` começa falso e vira verdadeiro no primeiro clique.
 *
 * ## Por que o botão aparece antes de o link existir
 *
 * A alternativa seria chamar a rota ao montar e desenhar o botão depois. Isso
 * inverte o custo: a ficha é aberta muitas vezes por dia, e o link é copiado
 * poucas — a maioria das consultas serviria para nada, e cada uma seria um
 * segredo a mais em memória.
 *
 * O 403 aqui é um fato e não um defeito: quem vê a mesa mas não é solicitante
 * nem responsável, e não tem `ver_qualquer`, não pode dar a conversa a
 * terceiros. A frase vem do servidor.
 */
function TrackingLink({ id, locked = false }: { id: number; locked?: boolean }) {
  const [asked, setAsked] = useState(false);

  const link = useQuery({
    queryKey: ['demandas', id, 'link'],
    queryFn: async () => ok(await api.tickets.trackingLink({ params: { id } }), 200),
    enabled: asked,
    /* o token não muda; reconsultar ao voltar para a aba não traria nada novo */
    staleTime: Infinity,
  });

  return (
    <div className="mt-5 border-t border-borda-suave pt-4">
      <p className="rotulo text-texto-tenue">Link de acompanhamento</p>
      <p className="mt-2 text-sm text-texto-suave">
        Abre o ticket sem pedir login. Serve para quem perguntou e não tem acesso ao
        portal.
      </p>

      {!asked && (
        <Button
          variant="contorno"
          size="sm"
          className="mt-3"
          /*
           * Sem ter aderido, o botão fica cinza e sem clique: o link abre a
           * CONVERSA sem login, e quem não a lê não a entrega a terceiros — é o
           * mesmo braço `read`/`copyLink` do domínio.
           */
          disabled={locked}
          onClick={() => setAsked(true)}
        >
          {/* `olho` e não `link`: o conjunto não tem ícone de link, e o que o
              botão faz é MOSTRAR — copiar é o botão seguinte. */}
          <Icon nome="olho" size={16} />
          Mostrar o link
        </Button>
      )}

      {asked && link.isPending && (
        <span className="esqueleto mt-3 block h-9 w-full max-w-md" />
      )}

      {asked && link.isError && (
        <Alert tone="alerta" className="mt-3">
          {link.error.message}
        </Alert>
      )}

      {link.data && (
        <div className="mt-3 flex flex-col gap-2">
          <p className="break-all font-mono text-xs text-texto">{link.data.url}</p>
          <CopyButton
            text={link.data.url}
            label="Copiar link"
            fallback="Não foi possível copiar por aqui. Selecione o endereço acima e copie à mão."
          />
        </div>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    /*
     * `cheia`, igual à tela. A régua do design cobra a coincidência: enquanto
     * os dois números batem ninguém vê, e no dia em que divergirem a coluna
     * muda de largura no instante em que o dado chega — foi exatamente o que
     * ela pegou aqui em 06/09, quando a tela virou duas colunas e o esqueleto
     * ficou na medida antiga.
     */
    <Page measure="cheia">
      <span className="esqueleto block h-7 w-72" />
      <Card>
        <span className="esqueleto block h-4 w-full max-w-md" />
        <span className="esqueleto mt-3 block h-4 w-full max-w-sm" />
      </Card>
      <Card>
        <span className="esqueleto block h-16 w-full" />
      </Card>
    </Page>
  );
}
